import { useEffect, useRef, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import type { JobLog } from '@/lib/types';

interface LogViewerProps {
  logs: JobLog[];
  autoScroll?: boolean;
}

export default function LogViewer({ logs, autoScroll = true }: LogViewerProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<number | 'all'>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Group logs by attempt number
  const attemptNumbers = useMemo(() => {
    const attempts = new Set(logs.map(log => log.attempt_number));
    return Array.from(attempts).sort((a, b) => b - a); // Descending order (latest first)
  }, [logs]);

  // Filter logs by selected attempt
  const filteredLogs = useMemo(() => {
    if (selectedAttempt === 'all') return logs;
    return logs.filter(log => log.attempt_number === selectedAttempt);
  }, [logs, selectedAttempt]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredLogs, autoScroll]);

  if (logs.length === 0) {
    return (
      <div className="log-viewer">
        <div className="text-center py-8 text-muted-foreground text-sm">
          No logs available
        </div>
      </div>
    );
  }

  const hasMultipleAttempts = attemptNumbers.length > 1;

  return (
    <div className="log-viewer">
      {/* Attempt selector - only show if multiple attempts exist */}
      {hasMultipleAttempts && (
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
          <span className="text-xs text-muted-foreground">
            {filteredLogs.length} log entries
          </span>
          <div ref={dropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1.5 px-2 py-1 rounded text-xs bg-muted/50 hover:bg-muted transition-colors"
            >
              <span>
                {selectedAttempt === 'all'
                  ? `All attempts (${attemptNumbers.length})`
                  : `Attempt ${selectedAttempt}${selectedAttempt === attemptNumbers[0] ? ' (latest)' : ''}`}
              </span>
              <ChevronDown className="h-3 w-3" />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-background rounded-lg border border-border shadow-lg overflow-hidden z-10">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAttempt('all');
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-xs hover:bg-muted transition-colors ${
                    selectedAttempt === 'all' ? 'bg-primary/10 text-primary' : ''
                  }`}
                >
                  All attempts ({attemptNumbers.length})
                </button>
                {attemptNumbers.map((attempt, index) => (
                  <button
                    key={attempt}
                    type="button"
                    onClick={() => {
                      setSelectedAttempt(attempt);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-muted transition-colors ${
                      selectedAttempt === attempt ? 'bg-primary/10 text-primary' : ''
                    }`}
                  >
                    Attempt {attempt}
                    {index === 0 && <span className="text-muted-foreground ml-1">(latest)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-0.5">
        {filteredLogs.map((log) => (
          <LogLine key={log.id} log={log} showAttempt={selectedAttempt === 'all' && hasMultipleAttempts} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

interface LogLineProps {
  log: JobLog;
  showAttempt?: boolean;
}

function LogLine({ log, showAttempt = false }: LogLineProps) {
  const timestamp = format(new Date(log.log_ts), 'HH:mm:ss.SSS');
  const levelClass = `log-${log.level}`;

  return (
    <div className="log-line font-mono text-xs leading-relaxed">
      <span className="log-timestamp">[{timestamp}]</span>
      {showAttempt && (
        <span className="text-muted-foreground">[A{log.attempt_number}]</span>
      )}
      <span className={`log-level ${levelClass}`}>[{log.level.toUpperCase()}]</span>
      <span className="log-message">{log.message}</span>
    </div>
  );
}
